import { useState } from 'react';
import { COPY } from '../content';
import { apiGet, apiPost, ApiError, type PayoutPreview } from '../api';
import { useAsync } from '../hooks';
import { money, dateOnly, pluralize } from '../format';
import { Spinner, LoadError, Card, Money, Empty, Sheet } from '../components/ui';

export function Payouts() {
  const { data, loading, error, reload } = useAsync(() => apiGet<PayoutPreview>('/v1/admin/payouts/preview'), []);
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);

  async function approveBatch() {
    setBusy(true);
    setMsg(null);
    try {
      const res = await apiPost<{ message?: string }>('/v1/admin/payouts/approve');
      setMsg(res.message ?? 'Batch approved.');
      reload();
    } catch (e) {
      // The gate returns 409 with a friendly message while execution is disabled.
      setMsg(e instanceof ApiError ? e.message : e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
      setConfirming(false);
    }
  }

  return (
    <div>
      <h1 className="screen-title">{COPY.payouts.title}</h1>
      <p className="screen-sub">{COPY.payouts.sub}</p>

      {loading ? <Spinner /> : null}
      {error ? <LoadError message={error} onRetry={reload} /> : null}

      {data ? (
        <>
          {!data.executionEnabled ? <div className="banner warn">{COPY.payouts.disabled}</div> : null}
          {msg ? <div className="banner info">{msg}</div> : null}

          <Card>
            <div className="row between">
              <div>
                <div className="muted small">Pays {dateOnly(data.payDateIso)}</div>
                <div className="screen-title" style={{ fontSize: 28 }}>
                  {money(data.totalCents)}
                </div>
                <div className="muted small">
                  {pluralize(data.items.length, 'member')} | covers {data.periodLabel}
                </div>
              </div>
              <button disabled={busy || !data.executionEnabled || data.items.length === 0} onClick={() => setConfirming(true)}>
                {COPY.payouts.approve}
              </button>
            </div>
            <div className="kv" style={{ marginTop: 10 }}>
              <span className="k">{COPY.payouts.storeCredit}</span>
              <span className="v">
                <Money cents={data.storeCreditCents} />
              </span>
            </div>
            <div className="kv">
              <span className="k">{COPY.payouts.cash}</span>
              <span className="v">
                <Money cents={data.cashCents} />
              </span>
            </div>
            <div className="kv">
              <span className="k">Minimum</span>
              <span className="v">{money(data.minPayoutCents)}</span>
            </div>
            <p className="muted small" style={{ marginTop: 6 }}>
              {COPY.payouts.minFromSettings}
            </p>
          </Card>

          {data.items.length === 0 ? <Empty>{COPY.payouts.empty}</Empty> : null}
          {data.items.map((i) => (
            <div className="card" key={i.memberId}>
              <div className="row between">
                <div>
                  <strong>{i.name}</strong>
                  <div className="small muted">
                    {i.method === 'store_credit' ? COPY.payouts.storeCredit : COPY.payouts.cash} | {pluralize(i.lineCount, 'sale')}
                  </div>
                </div>
                <Money cents={i.amountCents} />
              </div>
            </div>
          ))}

          {data.belowMinimum.length > 0 ? (
            <Card title={COPY.payouts.belowMin}>
              {data.belowMinimum.map((b) => (
                <div className="kv" key={b.memberId}>
                  <span className="k">{b.name}</span>
                  <span className="v">
                    <Money cents={b.amountCents} />
                  </span>
                </div>
              ))}
            </Card>
          ) : null}

          {confirming ? (
            <Sheet title={COPY.payouts.confirmTitle} onClose={() => setConfirming(false)}>
              <p>
                This pays <strong>{money(data.totalCents)}</strong> to {pluralize(data.items.length, 'member')} on {dateOnly(data.payDateIso)}.
              </p>
              <div className="preview small">
                {COPY.payouts.storeCredit}: {money(data.storeCreditCents)} | {COPY.payouts.cash}: {money(data.cashCents)}
              </div>
              <div className="row" style={{ marginTop: 12 }}>
                <button disabled={busy} onClick={approveBatch}>
                  {COPY.payouts.approve}
                </button>
                <button className="secondary" disabled={busy} onClick={() => setConfirming(false)}>
                  {COPY.common.cancel}
                </button>
              </div>
            </Sheet>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
